import React, { useEffect, useState, useRef, useCallback } from 'react'
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PauseIcon from '@mui/icons-material/Pause'
import SaveIcon from '@mui/icons-material/Save'
import TimerIcon from '@mui/icons-material/Timer'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '../../../../redux/store'
import { setRPMEnrollments } from '../../../../redux/patient-detail-slice'
import { PatientService } from '../../../../services/patient.service'
import { RPMEnrollment, RPMActivityType, RPM_ACTIVITY_DISPLAY, RPMStaffRole } from '../../../../model/rpm.types'
import { useCurrentUser } from '../../../../utils/use-current-user'
import { toast } from 'react-toastify'
import Helper from '../../../../utils/helper'

const patientService = new PatientService()

// Inactivity timeout in milliseconds (30 seconds)
const INACTIVITY_TIMEOUT = 30000

// localStorage key prefix for timer persistence
const TIMER_STORAGE_KEY = 'rpm-timer'

interface TimerState {
  patientId: string
  startTime: number      // timestamp when timer started running
  elapsedWhenPaused: number  // seconds elapsed when paused
  isRunning: boolean
}

// Helper to get/set timer state from localStorage
const getStoredTimerState = (patientId: string): TimerState | null => {
  try {
    const stored = localStorage.getItem(`${TIMER_STORAGE_KEY}-${patientId}`)
    if (stored) {
      const state = JSON.parse(stored) as TimerState
      // Validate it's for the same patient
      if (state.patientId === patientId) {
        return state
      }
    }
  } catch (e) {
    console.error('Failed to parse stored timer state:', e)
  }
  return null
}

const setStoredTimerState = (state: TimerState | null, patientId: string) => {
  try {
    if (state) {
      localStorage.setItem(`${TIMER_STORAGE_KEY}-${patientId}`, JSON.stringify(state))
    } else {
      localStorage.removeItem(`${TIMER_STORAGE_KEY}-${patientId}`)
    }
  } catch (e) {
    console.error('Failed to store timer state:', e)
  }
}

const RPMActivityTimer: React.FC = () => {
  const patientDetail = useSelector((state: RootState) => state.patientDetail.patientDetail)
  const enrollments = useSelector((state: RootState) => state.patientDetail.rpmEnrollments)
  const dispatch = useDispatch()
  const { isProvider } = useCurrentUser()
  const activeEnrollment = enrollments.find((e) => e.status === 'active') ?? null
  const [isRunning, setIsRunning] = useState(false)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [saveDialogOpen, setSaveDialogOpen] = useState(false)
  const [activityType, setActivityType] = useState<RPMActivityType>('data_review')
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [initialized, setInitialized] = useState(false)

  // Determine staff role from logged-in user
  const staffRole: RPMStaffRole = isProvider ? 'provider' : 'navigator'

  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null)
  const lastActivityRef = useRef<number>(Date.now())
  const startTimeRef = useRef<number>(0)

  // Fetch RPM enrollments and store in Redux (shared with RPMBillingComponent)
  useEffect(() => {
    if (patientDetail?.id) {
      patientService.getRPMEnrollments(patientDetail.id)
        .then((res) => {
          if (res?.data?.success) {
            dispatch(setRPMEnrollments(res.data.rpm_enrollments || []))
          }
        })
        .catch(() => dispatch(setRPMEnrollments([])))
    }
  }, [patientDetail?.id, dispatch])

  // Restore timer state from localStorage when patient changes
  useEffect(() => {
    if (!patientDetail?.id) return

    const patientId = String(patientDetail.id)
    const storedState = getStoredTimerState(patientId)

    if (storedState) {
      if (storedState.isRunning) {
        // Calculate how much time passed since the timer was running
        const now = Date.now()
        const additionalSeconds = Math.floor((now - storedState.startTime) / 1000)
        const totalElapsed = storedState.elapsedWhenPaused + additionalSeconds

        // Cap at a reasonable maximum (e.g., 2 hours) to handle stale data
        const maxSeconds = 2 * 60 * 60
        if (totalElapsed > maxSeconds) {
          // Timer was left running too long, pause with capped time
          setElapsedSeconds(maxSeconds)
          setIsRunning(false)
          toast.info('Timer was running for over 2 hours and has been paused', Helper.bottom_center())
        } else {
          // Restore elapsed time and auto-resume
          setElapsedSeconds(totalElapsed)
          setIsRunning(true)
          startTimeRef.current = now // Reset start time to now for accurate tracking
        }
      } else {
        // Timer was paused, restore elapsed time (stay paused)
        setElapsedSeconds(storedState.elapsedWhenPaused)
        setIsRunning(false)
      }
    } else {
      // No stored state, reset
      setElapsedSeconds(0)
      setIsRunning(false)
    }

    setInitialized(true)
  }, [patientDetail?.id])

  // Persist timer state to localStorage when state changes
  useEffect(() => {
    if (!patientDetail?.id || !initialized) return

    const patientId = String(patientDetail.id)

    if (isRunning) {
      // Update start time when starting
      if (startTimeRef.current === 0) {
        startTimeRef.current = Date.now()
      }
      setStoredTimerState({
        patientId,
        startTime: startTimeRef.current,
        elapsedWhenPaused: elapsedSeconds,
        isRunning: true,
      }, patientId)
    } else if (elapsedSeconds > 0) {
      // Paused with time
      setStoredTimerState({
        patientId,
        startTime: 0,
        elapsedWhenPaused: elapsedSeconds,
        isRunning: false,
      }, patientId)
    } else {
      // No time, clear storage
      setStoredTimerState(null, patientId)
    }
  }, [isRunning, elapsedSeconds, patientDetail?.id, initialized])

  // Auto-start timer when active enrollment is detected (only if no stored state)
  useEffect(() => {
    if (activeEnrollment && initialized && !isRunning && elapsedSeconds === 0) {
      const patientId = String(patientDetail?.id)
      const storedState = getStoredTimerState(patientId)
      // Only auto-start if there's no stored state (fresh session)
      if (!storedState) {
        setIsRunning(true)
        startTimeRef.current = Date.now()
      }
    }
  }, [activeEnrollment, initialized])

  // Handle activity detection
  const handleActivity = useCallback(() => {
    lastActivityRef.current = Date.now()

    // If timer was paused due to inactivity and user is active again, resume
    if (!isRunning && elapsedSeconds > 0) {
      // Don't auto-resume, let user manually resume
    }

    // Reset inactivity timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current)
    }

    if (isRunning) {
      inactivityTimerRef.current = setTimeout(() => {
        // Pause timer after inactivity
        setIsRunning(false)
      }, INACTIVITY_TIMEOUT)
    }
  }, [isRunning, elapsedSeconds])

  // Set up activity listeners
  useEffect(() => {
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart']

    events.forEach((event) => {
      window.addEventListener(event, handleActivity)
    })

    // Handle visibility change (tab switch)
    const handleVisibilityChange = () => {
      if (document.hidden && isRunning) {
        setIsRunning(false)
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Handle window blur
    const handleBlur = () => {
      if (isRunning) {
        setIsRunning(false)
      }
    }
    window.addEventListener('blur', handleBlur)

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity)
      })
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('blur', handleBlur)
    }
  }, [handleActivity, isRunning])

  // Timer tick
  useEffect(() => {
    if (isRunning) {
      timerRef.current = setInterval(() => {
        setElapsedSeconds((prev) => prev + 1)
      }, 1000)

      // Start inactivity timer
      inactivityTimerRef.current = setTimeout(() => {
        setIsRunning(false)
      }, INACTIVITY_TIMEOUT)
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current)
      }
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current)
    }
  }, [isRunning])

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const handleToggleTimer = () => {
    if (!isRunning) {
      // Starting timer
      startTimeRef.current = Date.now()
      handleActivity() // Reset inactivity timer when starting
    } else {
      // Pausing timer
      startTimeRef.current = 0
    }
    setIsRunning(!isRunning)
  }

  const handleOpenSaveDialog = () => {
    setIsRunning(false)
    setSaveDialogOpen(true)
  }

  const handleSaveTime = async () => {
    if (!patientDetail?.id || !activeEnrollment) return

    if (elapsedSeconds < 1) {
      toast.warning('No time to save', Helper.bottom_center())
      return
    }

    setSubmitting(true)
    try {
      const today = new Date()
      const activityDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`

      // Send both duration_minutes (for backwards compat) and duration_seconds
      await patientService.addRPMTimeEntry(patientDetail.id, {
        rpm_enrollment_id: activeEnrollment.id,
        activity_date: activityDate,
        duration_minutes: Math.ceil(elapsedSeconds / 60), // Backend expects this
        duration_seconds: elapsedSeconds,
        activity_type: activityType,
        staff_role: staffRole,
        notes: notes || undefined,
        is_auto_tracked: true,
      })

      toast.success(`Saved ${formatTime(elapsedSeconds)}`, Helper.bottom_center())
      setSaveDialogOpen(false)
      setElapsedSeconds(0)
      setNotes('')
      startTimeRef.current = 0

      // Clear localStorage
      if (patientDetail?.id) {
        setStoredTimerState(null, String(patientDetail.id))
      }

      // Dispatch event so RPM Billing card can refresh
      window.dispatchEvent(new CustomEvent('rpm-time-entry-saved'))
    } catch (err) {
      console.error('Failed to save time:', err)
      toast.error('Failed to save time', Helper.bottom_center())
    } finally {
      setSubmitting(false)
    }
  }

  const handleDiscard = () => {
    setSaveDialogOpen(false)
    setElapsedSeconds(0)
    setNotes('')
    startTimeRef.current = 0

    // Clear localStorage
    if (patientDetail?.id) {
      setStoredTimerState(null, String(patientDetail.id))
    }
  }

  // Don't render if no active RPM enrollment
  if (!activeEnrollment) return null

  const elapsedMinutes = Math.floor(elapsedSeconds / 60)

  return (
    <>
      <Tooltip title={isRunning ? 'RPM Timer - Running' : elapsedSeconds > 0 ? 'RPM Timer - Paused' : 'RPM Timer'}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.25,
            bgcolor: isRunning ? '#1b5e20' : elapsedSeconds > 0 ? '#e65100' : '#424242',
            borderRadius: 0.5,
            px: 0.5,
            py: 0,
            height: 22,
            transition: 'background-color 0.3s',
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontSize: '0.7rem', fontFamily: 'monospace', color: '#fff', lineHeight: 1 }}
          >
            {formatTime(elapsedSeconds)}
          </Typography>

          <IconButton size="small" onClick={handleToggleTimer} sx={{ p: 0, color: '#fff' }}>
            {isRunning ? (
              <PauseIcon sx={{ fontSize: '0.875rem' }} />
            ) : (
              <PlayArrowIcon sx={{ fontSize: '0.875rem' }} />
            )}
          </IconButton>

          {elapsedSeconds >= 60 && (
            <IconButton size="small" onClick={handleOpenSaveDialog} sx={{ p: 0, color: '#4fc3f7' }}>
              <SaveIcon sx={{ fontSize: '0.875rem' }} />
            </IconButton>
          )}
        </Box>
      </Tooltip>

      {/* Save Dialog */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Save Time Entry</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box mb={2}>
            <Typography variant="body2">
              Time tracked: <strong>{formatTime(elapsedSeconds)}</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Exact time is saved. Minutes are calculated at billing time.
            </Typography>
          </Box>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Activity Type</InputLabel>
            <Select
              value={activityType}
              onChange={(e) => setActivityType(e.target.value as RPMActivityType)}
              label="Activity Type"
            >
              {Object.entries(RPM_ACTIVITY_DISPLAY).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Notes (Optional)"
            size="small"
            multiline
            rows={2}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="What did you work on?"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" onClick={handleDiscard} color="error">
            Discard
          </Button>
          <Button size="small" onClick={() => setSaveDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSaveTime}
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={16} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default RPMActivityTimer
